import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import Modal from '../../components/common/Modal/Modal';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import AppIcon from '../../components/common/AppIcon/AppIcon';
import { useToast } from '../../context/ToastContext';
import {
  getAvailability,
  saveRules,
  addOverride,
  deleteOverride,
  connectCalendar,
  disconnectCalendar,
  getBookedSessions,
  cancelBookedSession
} from '../../services/mentorAvailabilityService';
import { getTimezoneOptions } from '../../utils/bookingTimezones';
import { getErrorMessage } from '../../utils/helpers';
import styles from './MentorAvailability.module.css';

// day_of_week matches the database: 0 = Sunday ... 6 = Saturday.
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DEFAULT_WINDOW = { start_time: '10:00', end_time: '13:00' };

const hhmm = (value) => String(value || '').slice(0, 5);

// The weekly pattern as the form holds it: one array of windows per weekday.
const draftFromRules = (rules = []) => {
  const draft = {};
  DAYS.forEach((_, day) => { draft[day] = []; });
  rules
    .filter((rule) => rule.is_available !== false)
    .forEach((rule) => {
      draft[rule.day_of_week] = draft[rule.day_of_week] || [];
      draft[rule.day_of_week].push({ start_time: hhmm(rule.start_time), end_time: hhmm(rule.end_time) });
    });
  Object.values(draft).forEach((windows) => windows.sort((a, b) => a.start_time.localeCompare(b.start_time)));
  return draft;
};

const draftToRules = (draft) => (
  Object.entries(draft).flatMap(([day, windows]) => windows.map((window) => ({
    day_of_week: Number(day),
    start_time: window.start_time,
    end_time: window.end_time
  })))
);

const sameRules = (a, b) => JSON.stringify(draftToRules(a)) === JSON.stringify(draftToRules(b));

const MentorAvailability = () => {
  const toast = useToast();

  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [draft, setDraft] = useState({});
  const [timezone, setTimezone] = useState('Asia/Kolkata');
  const [savingRules, setSavingRules] = useState(false);

  const [exception, setException] = useState({ specific_date: '', mode: 'off', start_time: '10:00', end_time: '13:00', reason: '' });
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const applyState = useCallback((next) => {
    setState(next);
    setDraft(draftFromRules(next.rules));
    setTimezone(next.timezone);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      applyState(await getAvailability());
    } catch (err) {
      console.error('Error loading availability:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [applyState]);

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      setSessions(await getBookedSessions());
    } catch (err) {
      console.error('Error loading booked sessions:', err);
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSessions(); }, [loadSessions]);

  const savedDraft = useMemo(() => draftFromRules(state?.rules), [state]);
  const dirty = state && (!sameRules(draft, savedDraft) || timezone !== state.timezone);

  // ---------------------------------------------------------------- weekly windows
  const addWindow = (day) => setDraft((prev) => ({
    ...prev,
    [day]: [...(prev[day] || []), { ...DEFAULT_WINDOW }]
  }));

  const removeWindow = (day, index) => setDraft((prev) => ({
    ...prev,
    [day]: prev[day].filter((_, i) => i !== index)
  }));

  const changeWindow = (day, index, field, value) => setDraft((prev) => ({
    ...prev,
    [day]: prev[day].map((window, i) => (i === index ? { ...window, [field]: value } : window))
  }));

  const copyToWeekdays = (day) => setDraft((prev) => {
    const source = (prev[day] || []).map((window) => ({ ...window }));
    const next = { ...prev };
    [1, 2, 3, 4, 5].forEach((weekday) => { next[weekday] = source.map((window) => ({ ...window })); });
    return next;
  });

  const handleSaveRules = async () => {
    setSavingRules(true);
    try {
      applyState(await saveRules({ timezone, rules: draftToRules(draft) }));
      toast.success('Availability saved.');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not save your availability.'));
    } finally {
      setSavingRules(false);
    }
  };

  // ---------------------------------------------------------------- date exceptions
  const handleAddException = async () => {
    if (!exception.specific_date) {
      toast.error('Pick a date for the exception.');
      return;
    }
    const custom = exception.mode === 'custom';
    try {
      applyState(await addOverride({
        specific_date: exception.specific_date,
        is_available: custom,
        start_time: custom ? exception.start_time : null,
        end_time: custom ? exception.end_time : null,
        reason: exception.reason || null
      }));
      setException({ specific_date: '', mode: 'off', start_time: '10:00', end_time: '13:00', reason: '' });
      toast.success('Exception added.');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not add that exception.'));
    }
  };

  const handleDeleteException = async (overrideId) => {
    try {
      applyState(await deleteOverride(overrideId));
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not remove that exception.'));
    }
  };

  // ---------------------------------------------------------------- calendar
  const handleConnect = async () => {
    try {
      applyState(await connectCalendar());
      toast.success('Calendar connected. Your busy time will now block slots.');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not connect your calendar.'));
    }
  };

  const handleDisconnect = async () => {
    try {
      applyState(await disconnectCalendar());
      toast.success('Calendar disconnected.');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not disconnect your calendar.'));
    }
  };

  // ---------------------------------------------------------------- sessions
  const sessionWhen = (session) => {
    const zone = state?.timezone || 'Asia/Kolkata';
    try {
      return new Intl.DateTimeFormat('en-GB', {
        weekday: 'short', day: 'numeric', month: 'short',
        hour: 'numeric', minute: '2-digit', hour12: true, timeZone: zone
      }).format(new Date(session.start_time_utc));
    } catch (err) {
      return session.start_time_utc;
    }
  };

  const handleCancelSession = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await cancelBookedSession(cancelTarget.id, cancelReason.trim() || undefined);
      setCancelTarget(null);
      setCancelReason('');
      toast.success('Session cancelled.');
      loadSessions();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not cancel that session.'));
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <h1 className={styles.title}>My Availability</h1>
        <div className={styles.loading}><LoadingSpinner size="large" message="Loading availability..." /></div>
      </div>
    );
  }

  if (error || !state) {
    return (
      <div className={styles.container}>
        <h1 className={styles.title}>My Availability</h1>
        <div className={styles.errorBox} role="alert">
          <span>Failed to load your availability. Please try again.</span>
          <Button size="small" variant="outline" onClick={load}>Retry</Button>
        </div>
      </div>
    );
  }

  const internal = state.gateway === 'internal';

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>My Availability</h1>
        <span className={`${styles.badge} ${internal ? styles.badgeInternal : styles.badgeExternal}`}>
          {internal ? 'Calendar booking' : 'WhatsApp booking'}
        </span>
      </div>
      <p className={styles.lede}>
        {internal
          ? 'Students pick from the hours you open below. A booked session is added straight to your Google Calendar with a Meet link, and the slot disappears for everyone else.'
          : 'Students pick from the hours you open below. A booked session becomes a class in your project and is scheduled on the project WhatsApp group, exactly like a class you create yourself.'}
      </p>

      {/* ----------------------------------------------------------------- calendar */}
      <Card className={styles.section}>
        <div className={styles.sectionHead}>
          <h2 className={styles.sectionTitle}><AppIcon name="calendar" size={18} /> Your calendar</h2>
        </div>
        {internal ? (
          <div className={styles.calendarRow}>
            <div>
              <p className={styles.calendarState}>
                {state.calendar_connected
                  ? `Connected: ${state.calendar_email}`
                  : `Not connected${state.email ? ` — ${state.email}` : ''}`}
              </p>
              <p className={styles.hint}>
                {state.calendar_connected
                  ? 'Anything already on your calendar now hides the slots it overlaps, so you will not be double-booked.'
                  : 'Connect your create-ed.in calendar so meetings you already have hide the slots they overlap.'}
              </p>
              {!state.calendar_configured && (
                <p className={styles.warning}>
                  Google Calendar is not configured on the server yet, so connecting will fail. Ask tech support.
                </p>
              )}
            </div>
            {state.calendar_connected
              ? <Button variant="outline" onClick={handleDisconnect}>Disconnect</Button>
              : <Button onClick={handleConnect} disabled={!state.calendar_configured}>Connect calendar</Button>}
          </div>
        ) : (
          <p className={styles.hint}>
            Your sessions are scheduled on the project WhatsApp group, so there is no calendar to connect.
            Just open up the hours you are free below.
          </p>
        )}
      </Card>

      {/* ----------------------------------------------------------------- weekly hours */}
      <Card className={styles.section}>
        <div className={styles.sectionHead}>
          <h2 className={styles.sectionTitle}><AppIcon name="clock" size={18} /> Weekly hours</h2>
          <div className={styles.timezoneRow}>
            <label htmlFor="availabilityTimezone" className={styles.timezoneLabel}>These hours are in</label>
            <select
              id="availabilityTimezone"
              className={styles.select}
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
            >
              {getTimezoneOptions().map((tz) => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.days}>
          {DAYS.map((label, day) => {
            const windows = draft[day] || [];
            return (
              <div key={label} className={styles.day}>
                <div className={styles.dayName}>{label}</div>
                <div className={styles.dayBody}>
                  {windows.length === 0 ? (
                    <span className={styles.unavailable}>Unavailable</span>
                  ) : windows.map((window, index) => (
                    <div key={index} className={styles.window}>
                      <input
                        type="time"
                        className={styles.time}
                        value={window.start_time}
                        aria-label={`${label} window ${index + 1} start`}
                        onChange={(e) => changeWindow(day, index, 'start_time', e.target.value)}
                      />
                      <span className={styles.dash}>–</span>
                      <input
                        type="time"
                        className={styles.time}
                        value={window.end_time}
                        aria-label={`${label} window ${index + 1} end`}
                        onChange={(e) => changeWindow(day, index, 'end_time', e.target.value)}
                      />
                      <button
                        type="button"
                        className={styles.iconButton}
                        aria-label={`Remove ${label} window ${index + 1}`}
                        onClick={() => removeWindow(day, index)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                <div className={styles.dayActions}>
                  <button type="button" className={styles.linkButton} onClick={() => addWindow(day)}>
                    + Add hours
                  </button>
                  {windows.length > 0 && (
                    <button type="button" className={styles.linkButton} onClick={() => copyToWeekdays(day)}>
                      Copy to Mon–Fri
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className={styles.saveRow}>
          <span className={styles.hint}>
            Sessions are {state.session_duration_minutes} minutes
            {state.buffer_minutes > 0 ? ` with a ${state.buffer_minutes} minute gap between them` : ''}.
            {draftToRules(draft).length === 0 && ' With no hours set, students cannot book you.'}
          </span>
          <Button onClick={handleSaveRules} disabled={!dirty} loading={savingRules}>
            Save hours
          </Button>
        </div>
      </Card>

      {/* ----------------------------------------------------------------- exceptions */}
      <Card className={styles.section}>
        <div className={styles.sectionHead}>
          <h2 className={styles.sectionTitle}>Date exceptions</h2>
        </div>
        <p className={styles.hint}>
          A one-off change for a single date: a day off, or different hours from your usual week.
        </p>

        <div className={styles.exceptionForm}>
          <input
            type="date"
            className={styles.input}
            aria-label="Exception date"
            value={exception.specific_date}
            onChange={(e) => setException((prev) => ({ ...prev, specific_date: e.target.value }))}
          />
          <select
            className={styles.select}
            aria-label="Exception type"
            value={exception.mode}
            onChange={(e) => setException((prev) => ({ ...prev, mode: e.target.value }))}
          >
            <option value="off">Day off</option>
            <option value="custom">Custom hours</option>
          </select>
          {exception.mode === 'custom' && (
            <>
              <input
                type="time"
                className={styles.time}
                aria-label="Exception start time"
                value={exception.start_time}
                onChange={(e) => setException((prev) => ({ ...prev, start_time: e.target.value }))}
              />
              <span className={styles.dash}>–</span>
              <input
                type="time"
                className={styles.time}
                aria-label="Exception end time"
                value={exception.end_time}
                onChange={(e) => setException((prev) => ({ ...prev, end_time: e.target.value }))}
              />
            </>
          )}
          <input
            type="text"
            className={styles.input}
            placeholder="Reason (optional)"
            aria-label="Exception reason"
            maxLength={120}
            value={exception.reason}
            onChange={(e) => setException((prev) => ({ ...prev, reason: e.target.value }))}
          />
          <Button size="small" onClick={handleAddException}>Add</Button>
        </div>

        {state.overrides.length === 0 ? (
          <p className={styles.empty}>No exceptions coming up.</p>
        ) : (
          <ul className={styles.overrideList}>
            {state.overrides.map((override) => (
              <li key={override.id} className={styles.overrideItem}>
                <span className={styles.overrideDate}>{override.specific_date}</span>
                <span className={styles.overrideWhat}>
                  {override.is_available
                    ? `Custom hours ${hhmm(override.start_time)}–${hhmm(override.end_time)}`
                    : 'Day off'}
                  {override.reason ? ` · ${override.reason}` : ''}
                </span>
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() => handleDeleteException(override.id)}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* ----------------------------------------------------------------- booked sessions */}
      <Card className={styles.section}>
        <div className={styles.sectionHead}>
          <h2 className={styles.sectionTitle}>Booked sessions</h2>
        </div>
        {sessionsLoading ? (
          <div className={styles.loadingInline}><LoadingSpinner message="Loading sessions..." /></div>
        ) : sessions.length === 0 ? (
          <p className={styles.empty}>No sessions booked yet.</p>
        ) : (
          <ul className={styles.sessionList}>
            {sessions.map((session) => (
              <li key={session.id} className={styles.sessionItem}>
                <div className={styles.sessionMain}>
                  <strong className={styles.sessionWhen}>{sessionWhen(session)}</strong>
                  <span className={styles.sessionWho}>
                    {session.student_name || 'Student'}
                    {session.project_name ? ` · ${session.project_name}` : ''}
                    {` · ${session.duration_minutes} min`}
                  </span>
                  {session.notes && <span className={styles.sessionNote}>“{session.notes}”</span>}
                </div>
                <div className={styles.sessionActions}>
                  {session.meet_link && (
                    <a className={styles.linkButton} href={session.meet_link} target="_blank" rel="noreferrer">
                      Join link
                    </a>
                  )}
                  {session.status === 'scheduled' ? (
                    <Button
                      size="small"
                      variant="outline"
                      onClick={() => { setCancelTarget(session); setCancelReason(''); }}
                    >
                      Cancel
                    </Button>
                  ) : (
                    <span className={styles.sessionStatus}>{session.status}</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Modal
        isOpen={Boolean(cancelTarget)}
        onClose={() => { if (!cancelling) setCancelTarget(null); }}
        title="Cancel this session?"
      >
        {cancelTarget && (
          <div className={styles.modalBody}>
            <p className={styles.hint}>
              {sessionWhen(cancelTarget)} with {cancelTarget.student_name || 'the student'}.
              {cancelTarget.booking_gateway === 'external'
                ? ' The class will be cancelled and the WhatsApp group told.'
                : ' The event will be removed from your Google Calendar and everyone notified.'}
            </p>
            <label htmlFor="cancelReason" className={styles.label}>Reason (optional)</label>
            <textarea
              id="cancelReason"
              className={styles.textarea}
              rows={3}
              maxLength={200}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            />
            <div className={styles.modalActions}>
              <Button variant="outline" onClick={() => setCancelTarget(null)} disabled={cancelling}>
                Keep it
              </Button>
              <Button onClick={handleCancelSession} loading={cancelling}>Cancel session</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MentorAvailability;
